import { createSelector } from '@reduxjs/toolkit';

import { productsSlugs } from 'constants/products';

import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  getHasAnsweredStylingGelQuestions,
  getHasAnsweredSupplementsQuestions,
  getHasCompletedHaircareConsultation,
} from 'dux/user/selectors';

// ---------------------------------------------------------
// SUPPLEMENTS USER FLOWS
// ---------------------------------------------------------

const shouldGoToFullConsultation = createSelector(
  getIsAuthenticated,
  getHasCompletedHaircareConsultation,
  (isAuthenticated, completedHaircareConsultation) =>
    !isAuthenticated || (isAuthenticated && !completedHaircareConsultation)
);

const shouldGoToSupplementsConsultation = createSelector(
  getIsAuthenticated,
  getHasCompletedHaircareConsultation,
  getHasAnsweredSupplementsQuestions,
  (isAuthenticated, completedHaircareConsultation, hasAnsweredSupplementsQuestion) =>
    isAuthenticated && completedHaircareConsultation && !hasAnsweredSupplementsQuestion
);

const shouldGoToStylingGelConsultation = createSelector(
  getIsAuthenticated,
  getHasCompletedHaircareConsultation,
  getHasAnsweredStylingGelQuestions,
  (isAuthenticated, completedHaircareConsultation, hasAnsweredStylingGelQuestion) =>
    isAuthenticated && completedHaircareConsultation && !hasAnsweredStylingGelQuestion
);

const shouldGoToCheckout = createSelector(
  getIsAuthenticated,
  getHasCompletedHaircareConsultation,
  getHasAnsweredSupplementsQuestions,
  getHasAnsweredStylingGelQuestions,
  (
    isAuthenticated,
    completedHaircareConsultation,
    hasAnsweredSupplementsQuestion,
    hasAnsweredStylingGelQuestion
  ) =>
    isAuthenticated &&
    completedHaircareConsultation &&
    hasAnsweredSupplementsQuestion &&
    hasAnsweredStylingGelQuestion
);

export const getSupplementsCTAProps = createSelector(
  shouldGoToFullConsultation,
  shouldGoToSupplementsConsultation,
  shouldGoToCheckout,
  (goToFullConsultation, goToSupplementsConsultation, goToCheckout) => {
    let to = '/consultation/haircare?from=supplements-page';
    let dataClick = 'consultation-haircare';

    if (goToFullConsultation) {
      to = '/consultation/haircare?from=supplements-page';
      dataClick = 'consultation-haircare';
    } else if (goToSupplementsConsultation) {
      to = '/consultation/haircare?questionSet=supplements';
      dataClick = 'finish-consultation';
    } else if (goToCheckout) {
      to = '/checkout/haircare';
      dataClick = 'order-prose';
    }

    const ctaTemplate = goToCheckout
      ? productName => `Order your ${productName}`
      : productName => `Customize your ${productName}`;

    return { to, dataClick, ctaTemplate };
  }
);

export const getStylingGelCTAProps = createSelector(
  shouldGoToFullConsultation,
  shouldGoToStylingGelConsultation,
  shouldGoToCheckout,
  (goToFullConsultation, goToStylingGelConsultation, goToCheckout) => {
    let to = '/consultation/haircare';
    let dataClick = 'consultation-haircare';

    if (goToFullConsultation) {
      to = '/consultation/haircare';
      dataClick = 'consultation-haircare';
    } else if (goToStylingGelConsultation) {
      to = '/consultation/haircare?questionSet=styling-gel';
      dataClick = 'finish-consultation';
    } else if (goToCheckout) {
      to = '/checkout/haircare';
      dataClick = 'order-prose';
    }

    const ctaTemplate = goToCheckout
      ? productName => `Order your ${productName}`
      : productName => `Customize your ${productName}`;

    return { to, dataClick, ctaTemplate };
  }
);

export const getAllCTAProps = createSelector(
  getSupplementsCTAProps,
  getStylingGelCTAProps,
  (supplementsCTAProps, stylingGelCTAProps) => ({
    [productsSlugs.SUPPLEMENT_CORE]: supplementsCTAProps,
    [productsSlugs.STYLING_GEL]: stylingGelCTAProps,
  })
);
